import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom'
import { apiClient } from './api/client'
import ScheduleMockup from './pages/ScheduleMockupFixed'
import TestSuggestions from './pages/TestSuggestions'
import { SuppliersDirectory } from './components/suppliers/SuppliersDirectory'
import { SupplierDashboard } from './components/suppliers/SupplierDashboard'
import { LoginScreen, ProtectedRoute, UnauthorizedPage } from './components/auth'
import { PWAManager } from './components/pwa'
import { ScheduleService } from './services/scheduleService'
import { useSchedulingConfig } from './hooks/useConfig'
import { useAuth } from './hooks/useAuth'
import clokdLogo from '/img/clokd_logo_tagline.png'
import './App.css'

function App() {
  const [healthStatus, setHealthStatus] = useState<string>('Checking...')
  const { data: schedulingConfig, isLoading: configLoading } = useSchedulingConfig()
  const { isAuthenticated, user, logout } = useAuth()

  useEffect(() => {
    checkHealth()
  }, [])

  useEffect(() => {
    // Update ScheduleService when config is loaded
    if (schedulingConfig && !configLoading) {
      ScheduleService.updateConfig(schedulingConfig)
      console.log('Configuration loaded from backend:', schedulingConfig)
    }
  }, [schedulingConfig, configLoading])

  const checkHealth = async () => {
    try {
      const response = await apiClient.get('/health')
      const status = response.data.status
      setHealthStatus(`API Status: ${status.charAt(0).toUpperCase() + status.slice(1)}`)
    } catch (error) {
      console.error('Health check failed:', error)
      setHealthStatus('API Status: Not Connected')
    }
  }

  return (
    <Router basename="/app">
      <div className="App">
        <header>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 0 0 0', marginBottom: '5px' }}>
            <img 
              src={clokdLogo} 
              alt="Clokd - Run the day" 
              style={{ height: '120px', width: 'auto' }}
            />
          </div>
          
          {isAuthenticated && (
            <nav>
              <Link to="/">Events</Link>
              <Link to="/calendar">Calendar</Link>
              <Link to="/teams">Teams</Link>
              <Link to="/reports">Reports</Link>
              <Link to="/settings">Settings</Link>
              <Link to="/schedule">Schedule</Link>
            </nav>
          )}
          
          <div className="status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{healthStatus}</span>
            {isAuthenticated && user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '14px' }}>
                  {user.name || user.email} ({user.role})
                </span>
                <button 
                  onClick={logout}
                  style={{
                    padding: '6px 12px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
                >
                  Logout
                </button>
              </div>
            ) : (
              <span style={{ fontSize: '14px', color: '#ef4444' }}>Not logged in</span>
            )}
          </div>
        </header>
        <main>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            
            {/* Protected routes - require authentication */}
            <Route path="/" element={
              <ProtectedRoute>
                <EventsDashboard />
              </ProtectedRoute>
            } />
            <Route path="/event/:eventId" element={
              <ProtectedRoute>
                <ScheduleMockup />
              </ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute>
                <CalendarView />
              </ProtectedRoute>
            } />
            <Route path="/teams" element={
              <ProtectedRoute>
                <TeamsDirectory />
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/schedule" element={
              <ProtectedRoute>
                <ScheduleMockup />
              </ProtectedRoute>
            } />
            <Route path="/test-suggestions" element={
              <ProtectedRoute>
                <TestSuggestions />
              </ProtectedRoute>
            } />
            <Route path="/supplier" element={
              <ProtectedRoute requiredRole="supplier">
                <SupplierDashboard />
              </ProtectedRoute>
            } />
            <Route path="/supplier/:eventId" element={
              <ProtectedRoute requiredRole="supplier">
                <SupplierDashboard />
              </ProtectedRoute>
            } />
          </Routes>
        </main>

        {/* PWA Manager for install prompts and updates */}
        <PWAManager 
          showInstallPromptAfterDelay={45000} 
          installPromptVariant="floating"
        />
      </div>
    </Router>
  )
}

function EventsDashboard() {
  const upcomingEvents = [
    { id: 'sarah-james-wedding', name: 'Sarah & James Wedding', date: 'Dec 15, 2024', venue: 'The Ritz Carlton', status: 'active' },
    { id: 'corp-gala-2024', name: 'Corporate Gala 2024', date: 'Dec 20, 2024', venue: 'Grand Ballroom', status: 'planning' },
    { id: 'nye-celebration', name: 'NYE Celebration', date: 'Dec 31, 2024', venue: 'Rooftop Terrace', status: 'planning' }
  ]

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>My Events</h1>
      <p style={{ color: '#666', marginBottom: '32px' }}>Manage and coordinate all your upcoming events</p>
      
      <div style={{ display: 'grid', gap: '20px' }}>
        {upcomingEvents.map(event => (
          <Link to={`/event/${event.id}`} key={event.id} style={{ textDecoration: 'none' }}>
            <div style={{
              padding: '24px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'
              e.currentTarget.style.transform = 'translateY(-2px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none'
              e.currentTarget.style.transform = 'translateY(0)'
            }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', color: '#1a1a1a' }}>{event.name}</h3>
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                  {event.date} • {event.venue}
                </p>
              </div>
              <div style={{
                padding: '4px 12px',
                background: event.status === 'active' ? '#10b981' : '#3b82f6',
                color: 'white',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                {event.status === 'active' ? 'Active' : 'Planning'}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

function CalendarView() {
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>Calendar View</h2>
      <p style={{ color: '#666' }}>Visual overview of all events across your venues</p>
    </div>
  )
}

function TeamsDirectory() {
  return <SuppliersDirectory />
}

function Reports() {
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>Reports & Analytics</h2>
      <p style={{ color: '#666' }}>Performance metrics and insights across all events</p>
    </div>
  )
}

function Settings() {
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <h2>Settings</h2>
      <p style={{ color: '#666' }}>Manage your account, integrations, and preferences</p>
    </div>
  )
}

export default App